// src/pages/attendance/AttendancePage.jsx

import React, { useState, useEffect, useContext, useMemo } from "react";
import { Clock, CheckCircle, XCircle, Timer } from "lucide-react";
import { Mo3tiCard, Mo3tiStatCard } from "./components/mo3tiComponents";
import { useTheme, ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";
import { apiFetch } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";
import AlertModal from "../../components/AlertModal";
import SaveSuccessModal from "../../components/SaveSuccessModal";
import FailureModal from "../../components/FailureModal";

import { translations } from "./constant/translations";
import { getAttendanceStyles } from "./styles/attendanceStyles";
import { useLocationState } from "./hooks/useLocationState";
import { useAttendanceData } from "./hooks/useAttendanceData";
import LocationDetailsPanel from "./components/LocationDetailsPanel";
import LocationIssues from "./components/LocationIssues";
import CheckInOutButtons from "./components/CheckInOutButtons";
import WFHModal from "./components/WFHModal";
import WeeklySection from "./components/WeeklySection";
import RemoteLocationPanel from "./components/RemoteLocationPanel";
import RemoteConsentModal from "./components/RemoteConsentModal";
import { useRemotePunch } from "./hooks/useRemotePunch";
import { devicePayload, isHandheldDevice } from "../../components/RemotePunch/remotePunchApi";
import { rpText } from "../../components/RemotePunch/remotePunchText";
import { useSearchParams } from "react-router-dom";
import MonthlyTable from "./components/MonthlyTable";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

const AttendancePage = ({ employeeData, attendanceData, onCheckIn, onCheckOut, currentUser }) => {
  const { currentTheme, isDarkMode } = useTheme();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.ar;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { profile } = useProfile();
  const infinityCode = profile?.infinityCode || localStorage.getItem("infinityCode");
  const employeeName = profile?.employeeName || null; // server-confirmed name only

  const styles = getAttendanceStyles(currentTheme, isDarkMode, language);

  // ── local UI state ──────────────────────────────────────────────
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showWFHModal, setShowWFHModal] = useState(false);
  const [wfhDate, setWfhDate] = useState(new Date().toISOString().split("T")[0]);
  const [wfhReason, setWfhReason] = useState("");
  const [isSubmittingWFH, setIsSubmittingWFH] = useState(false);
  const [wfhStatus, setWfhStatus] = useState(null);
  const [hasApprovedWFHToday, setHasApprovedWFHToday] = useState(false);
  const [isCheckingWFH, setIsCheckingWFH] = useState(true);
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);

  // Notification deep-link (?date=YYYY-MM-DD, e.g. ATTENDANCE_RESET) — the
  // affected day gets highlighted and scrolled to in the recent-entries table.
  const [searchParams] = useSearchParams();
  const focusDate = useMemo(() => {
    const d = searchParams.get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  }, [searchParams]);

  const showAlert = (title, message, variant = "info") =>
    setAlertModal({ open: true, title, message, variant });
  const closeAlert = () => setAlertModal((p) => ({ ...p, open: false }));

  // ── check WFH ───────────────────────────────────────────────────
  useEffect(() => {
    if (!infinityCode || !BASE_URL) { setIsCheckingWFH(false); return; }
    (async () => {
      try {
        setIsCheckingWFH(true);
        const today = new Date().toISOString().split("T")[0];
        const res = await apiFetch(`${BASE_URL}/api/leaves/wfh/check/${infinityCode}/${today}`, { method: "GET" });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.success && data.hasApprovedWFH) {
          setHasApprovedWFHToday(true);
          setWfhStatus("approved");
        } else if (data.hasPendingWFH) {
          setWfhStatus("pending");
        }
      } catch { /* silent */ } finally { setIsCheckingWFH(false); }
    })();
  }, [infinityCode, BASE_URL]);

  // ── Remote Punch ────────────────────────────────────────────────
  // A remote employee may punch from anywhere but must share their location
  // (even on an approved work-from-home day), from a phone or tablet. The
  // server enforces all of it; the page only explains and pre-empts.
  const remotePunch = useRemotePunch();
  const isRemote = remotePunch.isRemote;
  const rp = rpText(language);
  const [showConsent, setShowConsent] = useState(false);
  useEffect(() => {
    if (remotePunch.consentRequired) setShowConsent(true);
  }, [remotePunch.consentRequired]);
  const skipLocationForWFH = hasApprovedWFHToday && !isRemote;

  // ── hooks ───────────────────────────────────────────────────────
  const { locationState, distance, isOutOfZone, refreshLocation, retryLocation } = useLocationState({
    hasApprovedWFHToday: skipLocationForWFH,
    isCheckingWFH: isCheckingWFH || remotePunch.loading,
    infinityCode, BASE_URL, isRemote,
  });

  // Each lookup's problem shows as soon as that lookup settles, so a missing
  // work location isn't hidden behind a slow GPS fix (or vice versa). A remote
  // employee needs no work location, so only their GPS problems are shown.
  const locationIssues = [
    !isRemote && !locationState.isLoadingArea && locationState.areaError,
    !locationState.isLoadingUserLocation && locationState.gpsError,
  ].filter(Boolean);

  // Mirrors the server's rules so the buttons lock with a reason instead of
  // failing on press. Device and accuracy only count once monitoring is over.
  const enforcing = isRemote && !remotePunch.monitoring;
  const remoteAccuracy = locationState.userLocation?.accuracy;
  const remoteDeviceBlocked = enforcing && !isHandheldDevice();
  const remoteAccuracyBlocked =
    enforcing &&
    Number.isFinite(remotePunch.accuracyThresholdMeters) &&
    Number.isFinite(remoteAccuracy) &&
    remoteAccuracy > remotePunch.accuracyThresholdMeters;
  // The notice is not a lock: pressing a button reopens it until accepted.
  const remoteBlocked = isRemote && (!locationState.userLocation || remoteDeviceBlocked || remoteAccuracyBlocked);

  const {
    attendanceStats, loadingStats, weeklyData, monthlyAttendance,
    timeEntries, isLoadingTimeEntries,
    hasTodayCheckIn, todayCheckInDisplay,
    fetchTodayAttendanceRecord, fetchRecentTimeEntries,
  } = useAttendanceData({ currentUser, infinityCode, language, t, colors: currentTheme.colors });

  // ── initial page skeleton ────────────────────────────────────────
  // Show the full-page skeleton only until the first successful load —
  // afterwards `loadingStats`/`isLoadingTimeEntries` may flip true again
  // on refresh actions without re-showing the skeleton over live data.
  const isInitialLoading = loadingStats || isLoadingTimeEntries || isCheckingWFH || remotePunch.loading;
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isInitialLoading) setPageReady(true);
  }, [isInitialLoading]);

  // ── clock ───────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── check-in / check-out ────────────────────────────────────────
  // `source: "web"` tells the server this is a signed-in person punching from
  // the app, so it resolves `employeeNoString` as the real infinityCode instead
  // of treating it as a device-side clientCode.
  const buildRecord = (status) => ({
    source: "web",
    ipAddress: "127.0.0.1", portNo: 8000, protocol: "HTTP",
    macAddress: "web-interface", channelID: 1, activePostCount: 1,
    eventType: "AccessControllerEvent", eventState: "active",
    eventDescription: status === "checked-in"
      ? "Manual Check-in from Web Interface"
      : "Manual Check-out from Web Interface",
    deviceName: status === "checked-in" ? "Infinity Check-in" : "Infinity Check Out",
    majorEventType: 5, subEventType: 38,
    name: employeeName,
    cardReaderKind: 0, cardReaderNo: 1,
    verifyNo: Math.floor(Math.random() * 1000) + 200,
    employeeNoString: infinityCode,
    serialNo: Math.floor(Math.random() * 10000) + 14000,
    userType: "normal", currentVerifyMode: "manual",
    frontSerialNo: Math.floor(Math.random() * 10000) + 14000,
    attendanceStatus: status, label: "", statusValue: null,
    mask: "unknown", purePwdVerifyEnable: true,
  });

  // Raw position only — the server matches it against the allowed areas and
  // names the location itself, so the browser never asserts which site it is at.
  // Omitted when no fix is available; the server then records it as unknown.
  const buildGeo = () => {
    const loc = locationState?.userLocation;
    if (!loc || !Number.isFinite(loc.lat) || !Number.isFinite(loc.lng)) return {};
    return {
      geo: {
        latitude: loc.lat,
        longitude: loc.lng,
        ...(Number.isFinite(loc.accuracy) ? { accuracy: loc.accuracy } : {}),
      },
    };
  };

  // Remote punches also tell the server what it can't see itself (touch
  // points, device clock); regular punches are sent exactly as before.
  const buildPunchBody = (rec) =>
    JSON.stringify({ AccessControllerEvent: rec, ...rec, ...buildGeo(), ...(isRemote ? devicePayload() : {}) });

  // A refused remote punch carries a REMOTE_* code with its own explanation.
  const remoteRejection = async (res) => {
    if (res.ok) return null;
    const body = await res.json().catch(() => null);
    const code = body?.code;
    if (!code || !String(code).startsWith("REMOTE_")) return null;
    return rp.errors[code] || body?.messages?.[language] || body?.message || body?.error || null;
  };

  const handleStartTimer = async () => {
    if (isRemote && remotePunch.consentRequired) { setShowConsent(true); return; }
    setIsProcessing(true);
    try {
      const rec = buildRecord("checked-in");
      const res = await apiFetch(`${BASE_URL}/fingerprint/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: buildPunchBody(rec),
      });
      const refused = await remoteRejection(res);
      if (refused) {
        showAlert(language === "ar" ? "تعذّر تسجيل الحضور" : "Check-in refused", refused, "error");
        return;
      }
      if (!res.ok) throw new Error();
      setIsTimerRunning(true);
      await Promise.all([fetchTodayAttendanceRecord(), fetchRecentTimeEntries()]);
      setSuccessMessage(language === "ar" ? "تم تسجيل الحضور" : "Check In Done");
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2000);
    } catch {
      showAlert(
        language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "فشل تسجيل الحضور. يرجى المحاولة مرة أخرى." : "Check-in failed. Please try again.",
        "error"
      );
    } finally { setIsProcessing(false); }
  };

  const handleStopTimer = async () => {
    if (isRemote && remotePunch.consentRequired) { setShowConsent(true); return; }
    setIsProcessing(true);
    try {
      const rec = buildRecord("checked-out");
      const res = await apiFetch(`${BASE_URL}/fingerprint/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: buildPunchBody(rec),
      });
      const refused = await remoteRejection(res);
      if (refused) {
        showAlert(language === "ar" ? "تعذّر تسجيل الانصراف" : "Check-out refused", refused, "error");
        return;
      }
      if (!res.ok) throw new Error();
      setIsTimerRunning(false);
      await Promise.all([fetchTodayAttendanceRecord(), fetchRecentTimeEntries()]);
      setSuccessMessage(language === "ar" ? "تم تسجيل الانصراف" : "Check Out Done");
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2000);
    } catch {
      showAlert(
        language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "فشل تسجيل الانصراف. يرجى المحاولة مرة أخرى." : "Check-out failed. Please try again.",
        "error"
      );
    } finally { setIsProcessing(false); }
  };

  // ── WFH submit ──────────────────────────────────────────────────
  const handleSubmitWFH = async () => {
    if (!infinityCode || !String(infinityCode).trim()) {
      showAlert(language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "رمز الموظف غير صالح." : "Invalid employee code.", "error");
      return;
    }
    if (!wfhReason.trim()) {
      showAlert(language === "ar" ? "تنبيه" : "Warning",
        language === "ar" ? "يرجى إدخال سبب الطلب" : "Please enter a reason.", "error");
      return;
    }
    setIsSubmittingWFH(true);
    try {
      const res = await apiFetch(`${BASE_URL}/api/leaves`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          infinityCode: String(infinityCode),
          leaveType: "wfh", startDate: wfhDate, endDate: wfhDate,
          reason: wfhReason,
          myAdmin: profile?.firstLineManagerInfinityCode || null,
          language,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.message || "Failed to submit request");
      
      setSuccessMessage(t.workFromHome?.success || (language === "ar" ? "تم بنجاح" : "Success"));
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 2500);

      setShowWFHModal(false);
      setWfhReason("");
      setWfhStatus("pending");
    } catch (err) {
      const errorMsg = err.message || (t.workFromHome && t.workFromHome.error) || (language === "ar" ? "حدث خطأ" : "An error occurred");
      const lowerError = errorMsg.toLowerCase();
      // Use AlertModal if the error implies repetition or duplication
      if (
        lowerError.includes("already") || 
        lowerError.includes("pending") || 
        lowerError.includes("موجود") || 
        lowerError.includes("مسبق") ||
        lowerError.includes("تكرار")
      ) {
        showAlert(language === "ar" ? "خطأ" : "Error", errorMsg, "error");
      } else {
        setShowFailureModal(true);
        setTimeout(() => setShowFailureModal(false), 2500);
      }
    } finally { setIsSubmittingWFH(false); }
  };

  // ── format helpers (need language, kept here) ───────────────────
  const formatTime = (d) => d.toLocaleTimeString(language === "ar" ? "ar-EG" : "en-US", {
    hour12: true, hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const formatDate = (d) => d.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  // ── initial skeleton ────────────────────────────────────────────
  if (!pageReady && isInitialLoading) {
    const isRTL = language === "ar";
    const cardProps = { screenSize: { isMobile: false, isTablet: false }, isRTL };
    return (
      <div>
        <SkeletonStyles theme={isDarkMode ? "dark" : "light"} />
        <div style={styles.backgroundDecorative} />
        <div style={styles.contentWrapper}>
          <LoadingPill
            text={language === "ar" ? "جارٍ تحميل بيانات الحضور..." : "Loading attendance information..."}
            theme={isDarkMode ? "dark" : "light"}
            isRTL={isRTL}
          />

          {/* Time tracker skeleton */}
          <div style={styles.card1(cardProps)}>
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <SkeletonBox width="240px" height="56px" style={{ margin: "0 auto 16px" }} />
              <SkeletonBox width="180px" height="20px" style={{ margin: "0 auto 32px" }} />
              <div style={{ display: "flex", justifyContent: "center", gap: "20px", flexWrap: "wrap" }}>
                <SkeletonBox width="160px" height="52px" style={{ borderRadius: "16px" }} />
                <SkeletonBox width="160px" height="52px" style={{ borderRadius: "16px" }} />
              </div>
            </div>
          </div>

          {/* Stats grid skeleton */}
          <div style={styles.statsGrid}>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonStatCard key={i} cardStyle={styles.card1(cardProps)} />
            ))}
          </div>

          {/* Weekly skeleton */}
          <div style={styles.card1(cardProps)}>
            <SkeletonBox width="160px" height="26px" style={{ marginBottom: "20px" }} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonBox key={i} width="100%" height="180px" style={{ borderRadius: "20px" }} />
              ))}
            </div>
          </div>

          {/* Monthly table skeleton */}
          <div style={styles.card1(cardProps)}>
            <SkeletonBox width="200px" height="26px" style={{ marginBottom: "20px" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonBox key={i} width="100%" height="44px" style={{ borderRadius: "10px" }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── render ──────────────────────────────────────────────────────
  return (
    <div>
      <div style={styles.backgroundDecorative} />
      <div style={styles.contentWrapper}>

        {/* ── Time Tracker Card ── */}
        <div style={styles.card1({ screenSize: { isMobile: false, isTablet: false }, isRTL: language === "ar" })}>
        <div style={styles.timeTracker}>
          <div style={styles.timeTrackerDecoration} />
          <div style={styles.clockDisplay}>{formatTime(currentTime)}</div>
          <p style={styles.dateDisplay}>{formatDate(currentTime)}</p>

          <CheckInOutButtons
          
            isLoadingTimeEntries={isLoadingTimeEntries}
            isCheckingWFH={isCheckingWFH}
            isOutOfZone={isOutOfZone}
            hasApprovedWFHToday={hasApprovedWFHToday}
            remote={isRemote ? { blocked: remoteBlocked } : null}
            locationState={locationState}
            isTimerRunning={isTimerRunning}
            hasTodayCheckIn={hasTodayCheckIn}
            isProcessing={isProcessing}
            handleStartTimer={handleStartTimer}
            handleStopTimer={handleStopTimer}
            setShowWFHModal={setShowWFHModal}
            wfhStatus={wfhStatus}
            t={t}
            currentTheme={currentTheme}
            isDarkMode={isDarkMode}
            language={language}
            todayCheckInDisplay={todayCheckInDisplay}

          />

          {/* Location panel / loading / error */}
          {isRemote && !locationState.isLoadingUserLocation && locationState.userLocation && (
            <RemoteLocationPanel
              remotePunch={remotePunch}
              locationState={locationState}
              refreshLocation={refreshLocation}
              deviceBlocked={remoteDeviceBlocked}
              accuracyBlocked={remoteAccuracyBlocked}
              currentTheme={currentTheme}
              isDarkMode={isDarkMode}
              language={language}
            />
          )}

          {!isRemote && !hasApprovedWFHToday && !locationState.isLoadingArea && !locationState.isLoadingUserLocation
            && locationState.userLocation && locationState.allowedArea && (
              <LocationDetailsPanel
                locationState={locationState}
                distance={distance}
                isOutOfZone={isOutOfZone}
                refreshLocation={refreshLocation}
                currentTheme={currentTheme}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}

          {!skipLocationForWFH && (isRemote
            ? locationState.isLoadingUserLocation && !locationState.userLocation
            : locationState.isLoadingArea || locationState.isLoadingUserLocation) && (
            <div style={{ padding: "20px", textAlign: "center", color: currentTheme.colors.textSecondary }}>
              <div style={styles.spinner} />
              <div style={{ fontSize: "14px", fontFamily: "'Cairo', sans-serif" }}>
                {!isRemote && locationState.isLoadingArea ? t.timeTracker.loadingLocation : t.timeTracker.gettingLocation}
              </div>
            </div>
          )}

          {!skipLocationForWFH && (
            <LocationIssues
              issues={locationIssues}
              onRetry={retryLocation}
              currentTheme={currentTheme}
              isDarkMode={isDarkMode}
              t={t}
            />
          )}

          <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>
        </div>
        </div>
 {/* ── Stats Grid ── */}
        <div style={styles.statsGrid}>
          {loadingStats
            ? <div style={styles.loadingContainer}><p>{language === "ar" ? "جاري تحميل الإحصائيات..." : "Loading statistics..."}</p></div>
            : attendanceStats.map((stat, i) => (
              <Mo3tiStatCard key={i} icon={stat.icon} value={stat.value} label={stat.label}
                color={stat.color} trend={stat.trend} subtitle={stat.subtitle} theme={currentTheme} />
            ))
          }
        </div>
       

        {/* ── Weekly ── */}
        <WeeklySection
          weeklyData={weeklyData}
          t={t}
          currentTheme={currentTheme}
          isDarkMode={isDarkMode}
          language={language}
        />

        {/* ── Monthly + Recent ── */}
        <MonthlyTable
          monthlyAttendance={monthlyAttendance}
          timeEntries={timeEntries}
          isLoadingTimeEntries={isLoadingTimeEntries}
          t={t}
          currentTheme={currentTheme}
          isDarkMode={isDarkMode}
          language={language}
          focusDate={focusDate}
        />

      </div>

      {/* ── Remote Punch notice (once) ── */}
      <RemoteConsentModal
        open={isRemote && showConsent && remotePunch.consentRequired}
        onAccept={async () => { await remotePunch.acceptConsent(); setShowConsent(false); }}
        onClose={() => setShowConsent(false)}
        isDarkMode={isDarkMode}
        language={language}
      />

      {/* ── WFH Modal ── */}
      <WFHModal
        show={showWFHModal}
        onClose={() => setShowWFHModal(false)}
        wfhDate={wfhDate}
        setWfhDate={setWfhDate}
        wfhReason={wfhReason}
        setWfhReason={setWfhReason}
        isSubmittingWFH={isSubmittingWFH}
        onSubmit={handleSubmitWFH}
        t={t}
        currentTheme={currentTheme}
        isDarkMode={isDarkMode}
        language={language}
      />

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={isDarkMode ? "dark" : "light"}
        language={language}
      />

      <SaveSuccessModal
        isOpen={showSuccessModal}
        theme={isDarkMode ? "dark" : "light"}
        language={language}
        message={successMessage}
      />

      <FailureModal
        isOpen={showFailureModal}
        theme={isDarkMode ? "dark" : "light"}
        language={language}
      />
    </div>
  );
};

export default AttendancePage;